/**
 * SVG Image Source implementation
 *
 * Renders SVG files to PNG using resvg-js WASM, a high-performance Rust-based SVG renderer.
 */

import type { SizeSpec } from '@geom';
import { calculateFittedSize, MIP, Size } from '@geom';
import { canvasKit } from '@resources';
import { shortHashText } from '@utils/hash';
import { getSafeResvg, type ResvgRenderOptions } from '../resources/loadResvg';
import { Image } from './Image';
import type { ImageOrigin } from './ImageOrigin';
import type { ImageSource } from './ImageSource';

/**
 * SVG image source that renders SVG content to PNG using resvg-js
 */
export class SVGImageSource implements ImageSource {
  readonly id: string;
  readonly scalable = true; // SVG is scalable
  readonly origin?: ImageOrigin;
  readonly #svgContent: string;
  #intrinsicSize?: Size;

  constructor(svgContent: string, id?: string, origin?: ImageOrigin) {
    this.#svgContent = svgContent;
    this.id = id ?? `svg:${shortHashText(svgContent)}`;
    this.origin = origin ?? { type: 'svg', value: svgContent.substring(0, 64) };
  }

  load(): Promise<void> {
    if (this.#intrinsicSize) {
      // Already loaded
      return Promise.resolve();
    }
    try {
      // Get the WASM Resvg constructor
      const Resvg = getSafeResvg();

      // Create resvg renderer with SVG content to get intrinsic dimensions only
      const resvg = new Resvg(this.#svgContent);
      const bbox = resvg.getBBox();

      if (bbox) {
        this.#intrinsicSize = new Size(bbox.width, bbox.height);
      } else {
        // Fallback: render once just to get dimensions, don't store the data
        const rendered = resvg.render();
        this.#intrinsicSize = new Size(rendered.width, rendered.height);
      }

      // Don't pre-render the image data here - that belongs in render()
      // Just mark that we've successfully loaded and have intrinsic size
      return Promise.resolve();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return Promise.reject(new Error(`Failed to load SVG intrinsic size: ${message}`));
    }
  }

  get isLoaded(): boolean {
    return this.#intrinsicSize !== undefined;
  }

  get intrinsicSize(): Size {
    if (!this.#intrinsicSize) {
      throw new Error('SVGImageSource not loaded - call load() first');
    }
    return this.#intrinsicSize;
  }

  /**
   * Get the raw SVG content for vector embedding in SVGVisual
   */
  get svgContent(): string {
    return this.#svgContent;
  }

  render(sizeSpec?: SizeSpec): Image {
    if (!this.#intrinsicSize) {
      throw new Error('SVGImageSource not loaded - call load() first');
    }

    try {
      const Resvg = getSafeResvg();
      let options: ResvgRenderOptions;

      if (!sizeSpec) {
        // No size spec - render at intrinsic size
        options = {
          background: 'rgba(0, 0, 0, 0)', // Transparent background
          font: {
            loadSystemFonts: false
          },
          fitTo: {
            mode: 'original' // Use the SVG's intrinsic viewBox dimensions
          }
        };
      } else if (
        sizeSpec instanceof Size ||
        (typeof sizeSpec === 'object' && 'width' in sizeSpec && 'height' in sizeSpec)
      ) {
        // Direct Size specification - render to exact dimensions
        const targetSize =
          sizeSpec instanceof Size
            ? sizeSpec
            : (sizeSpec as { width: number; height: number });
        options = {
          background: 'rgba(0, 0, 0, 0)', // Transparent background
          font: {
            loadSystemFonts: false
          },
          fitTo: {
            mode: 'width', // Use width-based scaling for now
            value: targetSize.width
          }
        };
      } else {
        // SizeConstraints - calculate the fitted size and use width-based fitting
        // Use MIP.NONE for direct rendering to get exact requested size
        const fittedSize = calculateFittedSize(this.#intrinsicSize, sizeSpec, MIP.NONE);

        // Use width-based fitting if the fitted size differs from intrinsic
        if (!fittedSize.equals(this.#intrinsicSize)) {
          options = {
            background: 'rgba(0, 0, 0, 0)', // Transparent background
            font: {
              loadSystemFonts: false
            },
            fitTo: {
              mode: 'width',
              value: fittedSize.width
            }
          };
        } else {
          // Fallback to intrinsic size
          options = {
            background: 'rgba(0, 0, 0, 0)', // Transparent background
            font: {
              loadSystemFonts: false
            },
            fitTo: {
              mode: 'original'
            }
          };
        }
      }

      const resvg = new Resvg(this.#svgContent, options);
      const pngData = resvg.render();
      const pngBuffer = pngData.asPng();

      const ckImage = canvasKit.MakeImageFromEncoded(pngBuffer);
      if (!ckImage) {
        throw new Error('Failed to decode rendered SVG data');
      }
      return new Image(ckImage, this);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to render SVG with constraints: ${message}`);
    }
  }

  toString(): string {
    return `SVGImageSource(${this.#svgContent.length} chars)`;
  }

  /**
   * Convert to JSON representation
   */
  toJSON(): unknown {
    return {
      type: 'svg',
      id: this.id,
      origin: this.origin,
      svgContent: this.#svgContent
    };
  }
}

/**
 * Create SVG image source from file path
 */
export async function createSVGImageSourceFromFile(
  filePath: string
): Promise<SVGImageSource> {
  try {
    const fs = await import('fs/promises');
    const svgContent = await fs.readFile(filePath, 'utf-8');
    return new SVGImageSource(svgContent, `file:${filePath}`, {
      type: 'file',
      value: filePath
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to load SVG file ${filePath}: ${message}`);
  }
}
